import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  BarChart3,
  FileText,
  Users,
  Palette,
  Sparkles,
  LayoutTemplate,
  Settings,
  LogOut,
  Plus,
  Zap,
  ShieldCheck,
  Rocket,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { VerifyBanner } from "@/components/VerifyBanner";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Logo } from "@/components/Logo";
import { ADMIN_BASE } from "@/lib/adminPath";

const NAV = [
  { to: "/dashboard", label: "Analytics", icon: BarChart3, testid: "nav-analytics-link" },
  { to: "/proposals", label: "Proposals", icon: FileText, testid: "nav-proposals-link" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients-link" },
  { to: "/brand", label: "Brand Kits", icon: Palette, testid: "nav-brand-kit-link" },
  { to: "/templates", label: "Templates", icon: LayoutTemplate, testid: "nav-templates-link" },
  { to: "/ai", label: "AI Studio", icon: Sparkles, testid: "nav-ai-wizard-link" },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings-link" },
];

export const AppShell = ({ children, title, subtitle, actions }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    api.get("/billing/subscription").then((r) => setCredits(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-app text-zinc-100 flex">
      <aside className="hidden lg:flex w-60 shrink-0 flex-col border-r border-zinc-800/80 bg-app sticky top-0 h-screen">
        <div className="px-5 py-5 border-b border-zinc-800/70">
          <div className="flex items-center gap-2.5">
            <Logo size={28} testid="sidebar-logo" />
            <div>
              <p className="font-display font-extrabold text-sm tracking-tight">Propojo</p>
              <p className="text-[10px] font-num uppercase tracking-[0.18em] text-zinc-500">
                {user?.org_name || "Workspace"}
              </p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto nv-scroll">
          {NAV.map(({ to, label, icon: Icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors duration-150 ${
                  isActive
                    ? "bg-indigo-500/12 text-indigo-300 border border-indigo-500/25"
                    : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/50 border border-transparent"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {label}
            </NavLink>
          ))}
          {user?.platform_role && (
            <NavLink
              to={ADMIN_BASE}
              data-testid="nav-ops-console-link"
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-emerald-400/90 border border-emerald-500/25 bg-emerald-500/[0.06] hover:bg-emerald-500/[0.12] transition-colors duration-150 mt-2"
            >
              <ShieldCheck className="w-4 h-4" />
              Ops Console
            </NavLink>
          )}
        </nav>

        {credits && (
          <button
            data-testid="sidebar-credits-card"
            onClick={() => navigate("/plans")}
            className="mx-3 mb-3 rounded-xl border border-zinc-800 bg-zinc-900/60 p-3 text-left hover:border-indigo-500/40 transition-colors group"
          >
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-num uppercase tracking-[0.16em] text-zinc-500">Credits left</span>
              <Zap className="w-3.5 h-3.5 text-indigo-400" />
            </div>
            <p data-testid="sidebar-credits-left" className="mt-1 text-2xl font-bold font-num leading-none">
              {credits.proposals_left}
            </p>
            <div className="mt-2 h-1.5 rounded-full bg-zinc-800 overflow-hidden">
              <div
                className="h-full bg-indigo-500 transition-all duration-700"
                style={{ width: `${Math.min(100, (credits.proposals_used / Math.max(1, credits.proposal_limit)) * 100)}%` }}
              />
            </div>
            <p className="mt-1.5 text-[10px] font-num text-zinc-500">
              {credits.proposals_used}/{credits.proposal_limit} used · 1 credit = 1 proposal
            </p>
            <p className="mt-1 text-[11px] text-indigo-400 group-hover:text-indigo-300">
              Buy credits · ₹{credits.credit_price_inr || 15} each →
            </p>
          </button>
        )}

        {credits?.is_trial && (
          <div className="px-3 pb-3 -mt-1">
            <button
              data-testid="sidebar-upgrade-btn"
              onClick={() => navigate("/checkout?plan=monthly")}
              className="w-full h-10 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-medium inline-flex items-center justify-center gap-2 transition-colors"
            >
              <Rocket className="w-3.5 h-3.5" /> Upgrade plan
            </button>
            <p className="mt-1.5 text-center text-[10px] text-zinc-500">From ₹799/mo · cancel anytime</p>
          </div>
        )}

        <div className="p-3 border-t border-zinc-800/70">
          <div className="px-2 py-2 mb-2">
            <p className="text-sm text-zinc-200 truncate">{user?.name}</p>
            <p className="text-[11px] text-zinc-500 truncate">
              {user?.email} · <span className="uppercase font-num">{user?.role}</span>
            </p>
          </div>
          <button
            data-testid="logout-btn"
            onClick={async () => {
              await logout();
              navigate("/login");
            }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-zinc-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <VerifyBanner />
        <header className="sticky top-0 z-30 nv-glass border-b border-zinc-800/80">
          <div className="px-4 sm:px-8 py-4 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight truncate">{title}</h1>
              {subtitle && <p className="text-sm text-zinc-500 mt-0.5 truncate">{subtitle}</p>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {credits && (
                <span data-testid="header-credits-pill" className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-zinc-800 bg-zinc-900/60 px-3 h-9 text-xs font-num text-zinc-300">
                  <Zap className="w-3.5 h-3.5 text-indigo-400" />
                  {credits.proposals_left} credits
                </span>
              )}
              <ThemeToggle className="hidden sm:inline-flex" />
              {actions}
              <Button
                data-testid="create-proposal-btn"
                onClick={() => navigate("/ai")}
                className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg h-9"
              >
                <Plus className="w-4 h-4 mr-1.5" /> New proposal
              </Button>
            </div>
          </div>
          <div className="lg:hidden flex gap-1 px-3 pb-2 overflow-x-auto nv-scroll">
            {NAV.map(({ to, label }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  `px-3 py-1.5 rounded-full text-xs whitespace-nowrap border ${
                    isActive ? "border-indigo-500/40 text-indigo-300 bg-indigo-500/10" : "border-zinc-800 text-zinc-400"
                  }`
                }
              >
                {label}
              </NavLink>
            ))}
          </div>
        </header>
        <div className="px-4 sm:px-8 py-6 sm:py-8">{children}</div>
      </main>
    </div>
  );
};

export default AppShell;
